/**
 * Allow the user to enable and disable two-factor authentication as a setting.
 *
 * @module Two-Factor Authentication
 *
 * @import {Controller} from '@acng/frontend-stargazer'
 *
 * @see [2FA - Wikipedia](https://www.wikipedia.org/wiki/2FA)
 */

import {COMPILE, SCOPE} from '@acng/frontend-stargazer';
import {BOOLEAN, guard, OBJECT, STRING} from '@acng/frontend-rubicon';
import {ClassTokenController, RemoveController, ValueController} from 'nova';
import {OneTimeCodeFormController} from '../../layout/controller/one-time-code-form.js';

import {get, post, put} from '../../core/service/backend.js';

/**
 * Two Factor Auth Config Form
 *
 * @implements {Controller}
 */
class TwoFactorConfig extends OneTimeCodeFormController {
  active = new RemoveController(false);
  inactive = new RemoveController(true);
  style = new ClassTokenController('two-factor-disabled', 'two-factor-enabled');
  //#active = new ConditionBundle(false, this.active, this.inactive); //, this.style);
  qrcode = new ValueController();
  key = new ValueController();
  [SCOPE] = this;

  constructor() {
    super(async (form, code) => {
      const putActive = !this.#active;

      try {
        if (!this.#active) {
          await post('user/2fa-setup', {code}, form);
        } else {
          await put('user/2fa', {code, deactivate: true}, form);
        }

        this.#active = putActive;
      } catch (e) {
        await this.#fetchAll();

        return;
      }

      if (!putActive) {
        await this.#fetchSetupData();
      }
    });
  }

  get #active() {
    return this.active.condition;
  }

  set #active(value) {
    this.active.condition = value;
    this.inactive.condition = !value;
    this.style.condition = !value;
  }

  async [COMPILE]() {
    await this.#fetchAll();
  }

  async #fetchAll() {
    if (!(await this.#fetchActivationState())) {
      await this.#fetchSetupData();
    }
  }

  async #fetchSetupData() {
    const response = await get('user/2fa-setup');
    ASSERT: guard(response, OBJECT({svg: STRING, key: STRING}));

    this.qrcode.value = 'data:image/svg+xml;base64,' + response.svg;
    this.key.value = response.key;
  }

  /**
   * @returns {Promise<boolean>}
   */
  async #fetchActivationState() {
    const response = await get('user/2fa');
    ASSERT: guard(response, OBJECT({active: BOOLEAN}));

    const {active} = response;
    this.#active = active;

    return active;
  }
}

export {TwoFactorConfig};
